import React, {useEffect} from 'react'
import {SlotContentItem} from '../../../types/cms'
import {usePromotionDataTracking} from '../../../analytics/promotion-tracking'
import {Stack} from '../../../vanilla'
import {Slot} from '../../../components/cms/Slot'
import {SlotContentType} from '../../category'
import {DefaultContentBody} from 'dc-delivery-sdk-js'
import {DateUtils} from '../../../utils/date-utils'

export interface DisplaySlotsProps {
  slots: SlotContentItem
}

const CATEGORY_BOTTOM_SCHEMA = 'https://www.iceland.co.uk/slot/category-bottom-slot.json'
const CATEGORY_BANNER_SCHEMA = 'https://www.iceland.co.uk/slot/category-banner-slot.json'
const CATEGORY_HEADLINE_BANNER_SCHEMA = 'https://www.iceland.co.uk/content/category-headline-banner.json'
const IN_GRID_PROMO_SCHEMA = 'https://www.iceland.co.uk/slot/in-grid-positioning-slot.json'
const SEO_PAGE_CONTENT_SCHEMA = 'https://www.iceland.co.uk/slot/seo-page-content.json'

export const DisplaySlots = ({slots}: DisplaySlotsProps) => {
  const sendPromotionData = usePromotionDataTracking()

  const slotGroupName = slots._meta.name

  const promoContentForDataLayer = slots?.default
    ?.map((obj) => [
      {
        ...obj,
        slotGroupName: slotGroupName,
      },
    ])
    .filter((item) => Object.keys(item).length > 0)

  slots?.default?.forEach((slot) => {
    slot.slotGroupName = slotGroupName
  })

  useEffect(() => {
    if (promoContentForDataLayer) {
      sendPromotionData(promoContentForDataLayer)
    }
  }, [promoContentForDataLayer])

  return (
    <Stack gap={['10px', '16px']} marginBottom={['10px', '16px']}>
      <Slot data={slots} />
    </Stack>
  )
}

export const getBottomSlot = (slots?: SlotContentItem) => {
  const bottomSlot =
    slots &&
    slots?.length > 0 &&
    slots?.map(
      (slotGroup: SlotContentType, idx: number) =>
        slotGroup?._meta?.schema === CATEGORY_BOTTOM_SCHEMA && (
          <DisplaySlots slots={slotGroup} key={`${slotGroup?._meta?.deliveryId}-${idx}`} />
        ),
    )
  return bottomSlot
}

export const getBannerSlot = (data?: DefaultContentBody) => {
  let hasCategoryHeadlineBanner = false
  const bannerSlot =
    data?.slots &&
    data?.slots?.length > 0 &&
    data?.slots?.map((slotGroup: SlotContentType, idx: number) => {
      if (slotGroup?._meta?.schema === CATEGORY_BANNER_SCHEMA) {
        if (
          slotGroup.default?.find(({_meta}) => _meta.schema === CATEGORY_HEADLINE_BANNER_SCHEMA)
        ) {
          hasCategoryHeadlineBanner = true
        }
        return <DisplaySlots slots={slotGroup} key={`${slotGroup?._meta?.deliveryId}-${idx}`} />
      }
    })

  return {bannerSlot, hasCategoryHeadlineBanner}
}

// Check for In-Grid Promo Content and Filter out any expired Content
export const getInGridPromos = (page?: DefaultContentBody) => {
  const inGridPromos: SlotContentType =
    page?.slots &&
    page?.slots?.length > 0 &&
    page?.slots?.find((slotGroup: SlotContentType) => {
      if (slotGroup?._meta?.schema !== IN_GRID_PROMO_SCHEMA) {
        return false
      }

      // Check if we have a lifecycle expiry time
      if (!slotGroup?._meta?.lifecycle?.expiryTime) {
        // No Expiry time set, this must be default content, let it render
        return true
      }

      // Check we're between the start/end dates for the edition?
      const expiryTime = new Date(slotGroup._meta.lifecycle.expiryTime)

      // Should Return True if we haven't hit the expiry time yet
      return DateUtils.isBefore(new Date(), expiryTime)
    })
  return inGridPromos
}

export const getSeo = (page?: DefaultContentBody) => {
  // Find the seo content slot (category pages)
  const seoSlot = page?.slots?.find(
    (slot: {_meta?: {schema?: string}; seo?: {pageTitle?: string; pageDescription?: string}}) =>
      slot?._meta?.schema === SEO_PAGE_CONTENT_SCHEMA,
  )

  // Create the seo object
  const seo = {
    pageTitle: seoSlot?.seo?.pageTitle || page?.seo?.pageTitle || `${page?.name} | Iceland Foods`,
    pageDescription: seoSlot?.seo?.pageDescription || page?.seo?.pageDescription,
  }
  return seo
}
